import {memo, ReactElement, useEffect} from 'react'
import {useDispatch, useSelector} from 'react-redux'

import {useBrand} from '@vio/core/src/_web/brand/hooks/useBrand'
import {useBrandCapabilities} from '@vio/core/src/_web/brand/hooks/useBrandCapabilities'
import {useRouterProvider} from '@vio/core/src/_web/router/context/RouterContext'
import {removeOnAuthSuccessLandingUrl} from '@vio/core/src/auth/business/persistData'
import {
  getAuthenticationFlow,
  getPasswordlessLoginSourceComponent,
  getUnlockHotelId,
  isPasswordlessLoginVisible,
  selectIsAuthenticated,
  selectIsLoading
} from '@vio/core/src/auth/modules/selectors'
import {Connections} from '@vio/core/src/auth/modules/selectors.shared'
import {
  cleanUpLoginErrors,
  hidePasswordlessLogin
} from '@vio/core/src/auth/modules/slice'
import {makeSocialLogin} from '@vio/core/src/auth/modules/thunks'
import {endPreAuthAttempt} from '@vio/core/src/auth/services'
import {
  AuthenticationFlows,
  SourceComponentType
} from '@vio/core/src/auth/types/Auth'
import {Brand} from '@vio/core/src/brand/types'

import {setEmailSignUpVisibility} from '../../Email/slice'
import {useLogIn} from '../../hooks/useLogIn'
import {AUTHENTICATION_CLICK_TIMEOUT_BEFORE_NAVIGATING_AWAY} from '../constants'
import {
  canHandOffEmailSignInToApp,
  handOffEmailSignInToApp
} from '../handOffEmailSignInToApp'

export type HandleDesktopCodeSubmitProps = {
  codeOne: string
  codeTwo: string
  codeThree: string
  codeFour: string
}

export type HandleMobileCodeSubmitProps = {
  code: string
}

export type ChildrenProps = {
  isOpen: boolean
  authenticationFlow: AuthenticationFlows | null
  handleCloseOverlay: (options?: {isEmailHandoff?: boolean}) => void
  handleEmailLogin: () => void
  handleSocialLoginClick: (
    socialConnection: Connections,
    analyticsData?: Record<string, unknown>
  ) => void
}

interface PasswordlessLoginPropsType {
  languageCode: string
  anonymousId: string
  brand: Brand
  children: (props: ChildrenProps) => ReactElement<ChildrenProps>
}

const PasswordlessLogin = ({
  anonymousId,
  brand,
  children
}: PasswordlessLoginPropsType) => {
  const dispatch = useDispatch()
  const {replace} = useRouterProvider()
  const logIn = useLogIn()
  const {brandCode} = useBrand()
  const {
    auth: {hasOidcProvider}
  } = useBrandCapabilities()
  const isAuthenticated = useSelector(selectIsAuthenticated)
  const isLoading = useSelector(selectIsLoading)
  const isOpen = useSelector(isPasswordlessLoginVisible)
  const authenticationFlow = useSelector(getAuthenticationFlow)
  const unlockHotelId = useSelector(getUnlockHotelId)
  const sourceComponent = useSelector(getPasswordlessLoginSourceComponent)

  const handleCloseOverlay = (options?: {isEmailHandoff?: boolean}) => {
    if (!options?.isEmailHandoff) {
      endPreAuthAttempt()

      // The gate stored the checkout URL of the offer the user walked away
      // from; a later sign-in that does not set its own URL must not land there.
      if (authenticationFlow === AuthenticationFlows.PreCheckout) {
        removeOnAuthSuccessLandingUrl()
      }
    }

    dispatch(cleanUpLoginErrors())
    dispatch(hidePasswordlessLogin())
  }

  const handleEmailLogin = () => {
    handleCloseOverlay({isEmailHandoff: true})

    const emailSourceComponent = (sourceComponent ??
      SourceComponentType.Login) as SourceComponentType

    if (canHandOffEmailSignInToApp()) {
      handOffEmailSignInToApp({
        brand,
        sourceComponent: emailSourceComponent,
        flow: authenticationFlow
      })
      return
    }

    dispatch(
      setEmailSignUpVisibility({
        isOpen: true,
        sourceComponent: emailSourceComponent,
        flow: authenticationFlow
      })
    )
  }

  const handleSocialLoginClick = (
    socialConnection: Connections,
    incomingAnalyticsData?: Record<string, unknown>
  ) => {
    const analyticsData =
      socialConnection === Connections.OIDCFederation && hasOidcProvider
        ? {authenticationMethod: brandCode, ...incomingAnalyticsData}
        : incomingAnalyticsData || undefined

    setTimeout(
      () =>
        dispatch(
          makeSocialLogin({
            connection: socialConnection,
            unlockHotelId: unlockHotelId as string,
            // Same last resort as the email path above: persistence drops a
            // context with no source, which loses the origin entirely.
            sourceComponent: (sourceComponent ??
              SourceComponentType.Login) as SourceComponentType,
            flow: authenticationFlow,
            analyticsData
          })
        ),
      AUTHENTICATION_CLICK_TIMEOUT_BEFORE_NAVIGATING_AWAY
    )
  }

  useEffect(() => {
    const susiQueryParam = 'susi'
    const queryParams = new URLSearchParams(location?.search)
    const hasSusiInUrl = queryParams.get(susiQueryParam) === 'true'
    if (!isLoading && !isAuthenticated && hasSusiInUrl) {
      logIn({
        sourceComponent: SourceComponentType.Login,
        flow: AuthenticationFlows.SignUp,
        anonymousId
      })
      queryParams.set(susiQueryParam, 'false')
      replace({search: queryParams.toString()})
    }

    if (isAuthenticated && hasSusiInUrl) {
      queryParams.delete(susiQueryParam)
      replace({search: queryParams.toString()})
    }
  }, [isLoading, isAuthenticated, anonymousId, replace, logIn])

  return children({
    isOpen,
    authenticationFlow,
    handleCloseOverlay,
    handleSocialLoginClick,
    handleEmailLogin
  })
}

export default memo<PasswordlessLoginPropsType>(PasswordlessLogin)
